import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, filter, Observable, of, tap } from 'rxjs';
import { Page, PagedMetadata } from '../../shared/models/page-metadata.model';
import {
    CreateUser,
    DeleteUser,
    LazyLoadUsers,
    LoadUsers,
    LoadAssignableUsers,
    SetSearchTerm,
    SetUser,
    UpdateUser,
    LoadCsrUsers,
    LoadManagerUsers,
    QueryUsers
} from './user-management.action';
import { UserManagementService } from '../services/user-management.service';
import { IUser, Roles } from '@cod-monorepo/interfaces';
import { UserMappingOperations } from '../operations/user-mapping.operations';

const PAGE_SIZE = 20;

interface UserStateModel {
    search: string;
    users: IUser[];
    metadata: PagedMetadata | null;
    isLoading: boolean;
    isLazyLoading: boolean;
    assignableUsersLoading: boolean;
    assignableUsers: IUser[];
    csrLoading: boolean;
    csrUsers: IUser[];
    managersLoading: boolean;
    managers: IUser[];
    user?: IUser;
    profilePicture?: string;

    searchedUsers: { value: IUser; label: string }[];
}

@State<UserStateModel>({
    name: 'user',
    defaults: {
        search: '',
        users: [],
        metadata: null,
        isLoading: false,
        isLazyLoading: false,
        assignableUsersLoading: true,
        assignableUsers: [],
        csrLoading: true,
        csrUsers: [],
        managersLoading: true,
        managers: [],
        user: undefined,
        profilePicture: undefined,

        searchedUsers: []
    }
})
@Injectable()
export class UserState {
    constructor(private userService: UserManagementService) {}

    @Selector()
    static user(state: UserStateModel): IUser | undefined {
        return state?.user;
    }

    // Preperation for future implementation
    /*@Selector()
    static profilePicture(state: UserStateModel): string | undefined {
        return state.profilePicture;
    }*/

    @Selector()
    static isAdmin(state: UserStateModel): boolean | undefined {
        return state?.user?.roles?.includes(Roles.ADMIN);
    }

    @Selector()
    static allUsersLoaded(state: UserStateModel): boolean {
        return !(state?.metadata?.hasNextPage ?? true);
    }

    @Selector()
    static isLazyLoading(state: UserStateModel): boolean {
        return state.isLazyLoading;
    }

    @Selector()
    static isLoading(state: UserStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static users(state: UserStateModel): IUser[] {
        return state.users;
    }

    @Selector()
    static assignableUsers(state: UserStateModel): IUser[] {
        return state.assignableUsers;
    }

    @Selector()
    static assignableUsersLoading(state: UserStateModel): boolean {
        return state.assignableUsersLoading;
    }

    @Selector()
    static csrUsers(state: UserStateModel): IUser[] {
        return state.csrUsers;
    }

    @Selector()
    static csrLoading(state: UserStateModel): boolean {
        return state.csrLoading;
    }

    @Selector()
    static managers(state: UserStateModel): IUser[] {
        return state.managers;
    }

    @Selector()
    static managersLoading(state: UserStateModel): boolean {
        return state.managersLoading;
    }

    @Selector()
    static searchedUsers(state: UserStateModel): { value: IUser; label: string }[] {
        return state.searchedUsers;
    }

    @Action(LoadUsers)
    loadUsers(ctx: StateContext<UserStateModel>) {
        const { search } = ctx.getState();
        ctx.patchState({ isLoading: true });

        return this.userService.getUsers({ page: 0, pageSize: PAGE_SIZE, searchTerm: search }).pipe(
            catchError(() => {
                ctx.patchState({ isLoading: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    users: response.items.flat(),
                    isLoading: false
                })
            )
        );
    }

    @Action(LoadAssignableUsers)
    loadUsersWithRole(ctx: StateContext<UserStateModel>, action: LoadAssignableUsers) {
        ctx.patchState({ assignableUsersLoading: true });

        return this.userService.getUsers({ page: 0, pageSize: 300, role: action.role }).pipe(
            catchError(() => {
                ctx.patchState({ isLoading: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    assignableUsers: response.items.flat(),
                    assignableUsersLoading: false
                })
            )
        );
    }

    @Action(LoadCsrUsers)
    loadCsrUsers(ctx: StateContext<UserStateModel>) {
        ctx.patchState({ csrLoading: true });

        return this.userService.getUsers({ page: 0, pageSize: 300, role: Roles.CSR }).pipe(
            catchError(() => {
                ctx.patchState({ isLoading: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    csrUsers: response.items.flat(),
                    csrLoading: false
                })
            )
        );
    }

    @Action(LoadManagerUsers)
    loadManagerUsers(ctx: StateContext<UserStateModel>) {
        ctx.patchState({ managersLoading: true });

        return this.userService.getUsers({ page: 0, pageSize: 300, role: Roles.MANAGER }).pipe(
            catchError(() => {
                ctx.patchState({ isLoading: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    managers: response.items.flat(),
                    managersLoading: false
                })
            )
        );
    }

    @Action(LazyLoadUsers)
    lazyLoadUsers(ctx: StateContext<UserStateModel>): Observable<Page<IUser>> {
        const { search } = ctx.getState();
        const state = ctx.getState();
        const page = Math.round(ctx.getState().users.length / PAGE_SIZE);
        ctx.patchState({ isLazyLoading: true });

        return this.userService.getUsers({ page, pageSize: PAGE_SIZE, searchTerm: search }).pipe(
            catchError(() => {
                ctx.patchState({ isLazyLoading: false });
                return of({ items: [], metadata: {} } as unknown as Page<IUser>);
            }),
            tap(result =>
                ctx.patchState({
                    users: state.users.concat(result.items.flat()),
                    metadata: result.metadata,
                    isLazyLoading: false
                })
            )
        );
    }

    @Action(SetSearchTerm)
    setSearchTerm(ctx: StateContext<UserStateModel>, action: SetSearchTerm) {
        ctx.patchState({ search: action.searchTerm });
        ctx.dispatch(new LoadUsers());
    }

    @Action(UpdateUser)
    updateUser(ctx: StateContext<UserStateModel>, action: UpdateUser) {
        return this.userService.updateUser(action.gid, action.user).pipe(
            tap(() => {
                ctx.patchState({
                    users: ctx
                        .getState()
                        .users.map(user => (user.gid === action.gid ? { ...user, ...action.user } : user))
                });
            })
        );
    }

    @Action(DeleteUser)
    deleteUser(ctx: StateContext<UserStateModel>, action: DeleteUser) {
        return this.userService.deleteUser(action.gid).pipe(
            tap(() => {
                ctx.patchState({
                    users: ctx.getState().users.filter(user => user.gid !== action.gid)
                });
            })
        );
    }

    @Action(CreateUser)
    createUser(ctx: StateContext<UserStateModel>, action: CreateUser) {
        return this.userService.createUser(action.user).pipe(
            tap(response => {
                ctx.patchState({
                    users: ctx.getState().users.concat(response)
                });
            })
        );
    }

    @Action(SetUser)
    public setUser({ patchState }: StateContext<UserStateModel>, { payload }: SetUser) {
        return of(patchState({ user: payload.user }));
    }

    // Preperation for future implementation
    /*@Action(LoadProfilePicture)
    public setProfilePicture({ patchState }: StateContext<UserStateModel>) {
        return this.userService.getProfilePicture().pipe(tap(profilePicture => patchState({ profilePicture })));
    }*/

    @Action(QueryUsers)
    loadDelegatableUsers(context: StateContext<UserStateModel>, action: QueryUsers) {
        const { search, page } = action;

        return this.userService.getUsers({ page, pageSize: PAGE_SIZE, searchTerm: search }).pipe(
            tap(users => {
                if (page === 0) {
                    context.patchState({
                        searchedUsers: UserMappingOperations.mapToLabeledUser(users.items)
                    });
                } else {
                    context.patchState({
                        searchedUsers: [
                            ...context.getState().searchedUsers,
                            ...UserMappingOperations.mapToLabeledUser(users.items)
                        ]
                    });
                }
            })
        );
    }
}
